// Public read of site_config (trust + routing + compliance) with a tiny
// localStorage cache so the homepage paints with the most recent content
// without waiting for a network round-trip on every render.

import { useEffect, useState } from "react";
import {
  getPublicConfig,
  defaultTrustContent,
  defaultRouting,
  defaultCompliance,
  type TrustContent,
  type RoutingTargets,
  type ComplianceContent,
} from "@/lib/site-content.functions";
import { type CtaMode, DEFAULT_CTA_MODE, normaliseCtaMode } from "@/lib/cta-mode";

export const PUBLIC_CONFIG_SCHEMA_VERSION = 3;
export const PUBLIC_CONFIG_CACHE_KEY = `woodseer_public_config_v${PUBLIC_CONFIG_SCHEMA_VERSION}`;
const LEGACY_CACHE_KEYS = ["woodseer_public_config_v1", "woodseer_public_config_v2"];

interface CachedConfig {
  trust: TrustContent;
  routing: RoutingTargets;
  compliance: ComplianceContent;
  ctaMode: CtaMode;
  cachedAt: number;
}

function warnCacheIssue(message: string) {
  if (typeof window !== "undefined" && import.meta.env.DEV) console.warn(message);
}

function readCache(): CachedConfig | null {
  if (typeof window === "undefined") return null;
  try {
    for (const key of LEGACY_CACHE_KEYS) localStorage.removeItem(key);
    const raw = localStorage.getItem(PUBLIC_CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedConfig>;
    // Defensive merge: older cached payloads may be missing newer fields
    // (e.g. compliance.motAvailability shipped after this cache key existed).
    // Always layer cache on top of defaults so a stale cache cannot crash
    // components that destructure newer keys before the network refresh
    // hydrates real values.
    return {
      trust: { ...defaultTrustContent, ...(parsed.trust || {}) },
      routing: { ...defaultRouting, ...(parsed.routing || {}) },
      compliance: {
        ...defaultCompliance,
        ...(parsed.compliance || {}),
        motAvailability: {
          ...defaultCompliance.motAvailability,
          ...((parsed.compliance as ComplianceContent | undefined)?.motAvailability || {}),
        },
      },
      ctaMode: normaliseCtaMode(parsed.ctaMode),
      cachedAt: parsed.cachedAt || 0,
    };
  } catch {
    warnCacheIssue("Woodseer public config cache was invalid and has been ignored.");
    try { localStorage.removeItem(PUBLIC_CONFIG_CACHE_KEY); } catch {}
    return null;
  }
}

function writeCache(value: { trust: TrustContent; routing: RoutingTargets; compliance: ComplianceContent; ctaMode: CtaMode }) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      PUBLIC_CONFIG_CACHE_KEY,
      JSON.stringify({ ...value, cachedAt: Date.now() } satisfies CachedConfig)
    );
  } catch {
    /* ignore quota errors */
  }
}

export function usePublicConfig() {
  // SSR stability: first server render and first client render must match.
  // Cached/browser-only config is applied only after mount to avoid hydration
  // mismatches from stale localStorage (e.g. old postcode/address values).
  const [trust, setTrust] = useState<TrustContent>(defaultTrustContent);
  const [routing, setRouting] = useState<RoutingTargets>(defaultRouting);
  const [compliance, setCompliance] = useState<ComplianceContent>(defaultCompliance);
  const [ctaMode, setCtaMode] = useState<CtaMode>(DEFAULT_CTA_MODE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = readCache();
    if (cached && !cancelled) {
      setTrust(cached.trust);
      setRouting(cached.routing);
      setCompliance(cached.compliance);
      setCtaMode(cached.ctaMode);
    }
    getPublicConfig()
      .then((res) => {
        if (cancelled) return;
        setTrust(res.trust);
        setRouting(res.routing);
        setCompliance(res.compliance);
        setCtaMode(res.ctaMode);
        writeCache({ trust: res.trust, routing: res.routing, compliance: res.compliance, ctaMode: res.ctaMode });
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { trust, routing, compliance, ctaMode, loaded };
}

/** Returns the active CTA mode with SSR-safe defaulting. */
export function useCtaMode(): CtaMode {
  return usePublicConfig().ctaMode;
}

export function clearPublicConfigCache() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(PUBLIC_CONFIG_CACHE_KEY);
    for (const key of LEGACY_CACHE_KEYS) localStorage.removeItem(key);
  } catch {}
}

/** Synchronously read the cached routing targets (no SSR / no fetch). Falls back to defaults. */
export function getCachedRouting(): RoutingTargets {
  return readCache()?.routing || defaultRouting;
}

/** Synchronously read the cached compliance block (no SSR / no fetch). Falls back to defaults. */
export function getCachedCompliance(): ComplianceContent {
  return readCache()?.compliance || defaultCompliance;
}
